import React from 'react';
import { Box, List, ListItem, ListItemText, Tooltip } from '@mui/material';
import { SmartToy } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@/store';
import { Agent } from '@/models/agent';
import { createConversation, listConversations, updateConversation } from '@/api/conversations';
import { formatNewChatTitle } from '@/utils/dateUtils';
import { SectionHeader } from './SidebarComponents';
import { invalidateConversationCollections } from '@/lib/queryInvalidation';
import { queryKeys } from '@/lib/queryKeys';

interface AgentSectionProps {
  agents: Agent[];
  isOpen: boolean;
  onToggle: () => void;
  selectedAgent: string | null;
  onSelectAgent: (agent: Agent) => void;
  hasModelAccess?: boolean;
}

const AgentSection: React.FC<AgentSectionProps> = ({
  agents,
  isOpen,
  onToggle,
  selectedAgent,
  onSelectAgent,
  hasModelAccess = true
}) => {
  const queryClient = useQueryClient();

  const {
    setSelectedConversation,
    addNewConversation,
    setCurrentFeature,
    setConversationList,
    selectedConversation,
    messages,
  } = useAppStore();

  const setSelectedAgent = useAppStore(s => s.setSelectedAgent);

  const refreshConversations = async () => {
    await invalidateConversationCollections(queryClient);
    return queryClient.fetchQuery({
      queryKey: queryKeys.conversations.list(),
      queryFn: listConversations,
      staleTime: 0,
    });
  };

  const handleAgentSelect = async (agent: Agent) => {
    try {
      const currentMessages = messages ?? [];
      const currentConversationHasMessages = currentMessages.length > 0;

      let conversationId: string;

      if (!currentConversationHasMessages && selectedConversation?.id) {
        // Current chat is empty — repurpose it for this agent
        await updateConversation(selectedConversation.id, {
          title: formatNewChatTitle(),
          agent_key: agent.id,
          updated_date: new Date().toISOString()
        });
        conversationId = selectedConversation.id;
      } else {
        // Current chat has messages — look for any other empty chat to repurpose
        const freshConversations = await refreshConversations();
        const emptyChat = freshConversations
          .filter(conv =>
            conv.id !== selectedConversation?.id &&
            (conv.message_count === 0 || conv.message_count === undefined)
          )
          .sort((a, b) =>
            new Date(b.updated_date || b.created_date).getTime() -
            new Date(a.updated_date || a.created_date).getTime()
          )[0];

        if (emptyChat) {
          // Repurpose the empty chat for this agent
          await updateConversation(emptyChat.id, { title: formatNewChatTitle(), agent_key: agent.id });
          conversationId = emptyChat.id;
        } else {
          // No empty chat found — create a new one
          const newConversation = await createConversation({
            title: formatNewChatTitle(),
            agent_key: agent.id
          });
          addNewConversation(newConversation);
          conversationId = newConversation.id;
        }
      }

      // Refresh list and select the target conversation
      const updatedConversations = await refreshConversations();
      setConversationList(updatedConversations);
      const conversation = updatedConversations.find(c => c.id === conversationId);
      if (conversation) {
        useAppStore.setState({ messages: [] });
        setSelectedConversation(conversation);
      }

      setSelectedAgent(agent.id);
      setCurrentFeature(null);
      onSelectAgent(agent);
    } catch (error) {
      console.error('Failed to select agent:', error);
    }
  };

  return (
    <Box>
      <SectionHeader
        title="Agents"
        isOpen={isOpen}
        onToggle={onToggle}
        disabled={!hasModelAccess}
      />
      
      {isOpen && (
        <List dense disablePadding sx={{ 
          maxHeight: 48 * 5, 
          overflowY: 'auto', 
          transition: 'max-height 0.2s', 
          mb: 1,
          '&::-webkit-scrollbar': {
            width: '3px',
          },
          '&::-webkit-scrollbar-track': {
            display: 'none'
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.35)',  // Updated to match the screenshot
            borderRadius: '10px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.45)'  // Updated hover state
            }
          }
        }}>
          {agents.map((agent) => (
            <Tooltip
              key={agent.id}
              title={agent.description || `${agent.name}`}
              placement="right"
              arrow
            >
              <ListItem
                component="div"
                sx={{
                  borderRadius: selectedAgent === agent.id ? 0 : 3,
                  color: 'primary.main',
                  bgcolor: (theme) => selectedAgent === agent.id 
                    ? theme.palette.mode === 'light' 
                      ? 'rgba(0, 0, 0, 0.12)' 
                      : 'rgba(255, 255, 255, 0.12)' 
                    : 'transparent',
                  fontWeight: selectedAgent === agent.id ? 700 : 500,
                  mb: 1,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: 'background 0.2s, border-radius 0.2s',
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.08)'
                      : 'rgba(255, 255, 255, 0.08)',
                    borderRadius: 0,
                  },
                }}
                onClick={() => handleAgentSelect(agent)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {agent.icon ? (
                    <Box sx={{ mr: 1, fontSize: 20 }}>
                      {agent.icon}
                    </Box>
                  ) : (
                    <SmartToy sx={{ mr: 1, color: 'text.primary' }} fontSize="small" />
                  )}
                  <ListItemText
                    primary={agent.name}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: selectedAgent === agent.id ? 700 : 500,
                      noWrap: true,
                      color: 'text.primary',
                    }}
                  />
                </Box>
              </ListItem>
            </Tooltip>
          ))}
        </List>
      )}
      <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', my: 2 }} />
    </Box>
  );
};

export default AgentSection;
